import './toolbar.css';
import { tagNumber, type SeriesRef } from '../dicomweb';
import type { ViewerShell } from '../shell';
import type { Viewport } from '../viewport';
import { setCineIndicator } from '../overlay';

/**
 * Cine (spec sections 2 row 26 and 5): play/pause over the active viewport's stack, looping,
 * FPS slider 1–60 (default 30) and "auto-fps" (on by default) that uses the DICOM frame rate.
 */

const DEFAULT_FPS = 30;
const MIN_FPS = 1;
const MAX_FPS = 60;
const FPS_STEP = 1;
const MS_PER_SECOND = 1000;
const TAG_RECOMMENDED_FRAME_RATE = '00082144';
const TAG_FRAME_TIME = '00181063';
const TAG_CINE_RATE = '00180040';

let shellRef: ViewerShell | null = null;
let playingOn: Viewport | null = null;
let barVisible = false;
let autoFps = true;
let manualFps = DEFAULT_FPS;
let bar: HTMLElement | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  refreshBar();
  listeners.forEach((listener) => listener());
}

/** Called when play state or bar visibility changes. */
export function onCineChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function isCinePlaying(): boolean {
  return playingOn !== null;
}

export function isCineBarVisible(): boolean {
  return barVisible;
}

/** " Cine" in Más: shows or hides the cine bar in the toolbar. */
export function setCineBarVisible(visible: boolean): void {
  barVisible = visible;
  notify();
}

/**
 * DICOM frame rate of a series: Recommended Display Frame Rate, else 1000 / Frame Time, else Cine Rate.
 * Returns undefined when the headers are not loaded (parity mode keeps no metadata).
 */
export function dicomFrameRate(series: SeriesRef | undefined): number | undefined {
  for (const instance of series?.instances ?? []) {
    const meta = instance.metadata;
    if (!meta) continue;
    const recommended = tagNumber(meta, TAG_RECOMMENDED_FRAME_RATE);
    if (recommended > 0) return recommended;
    const frameTime = tagNumber(meta, TAG_FRAME_TIME);
    if (frameTime > 0) return MS_PER_SECOND / frameTime;
    const cineRate = tagNumber(meta, TAG_CINE_RATE);
    if (cineRate > 0) return cineRate;
  }
  return undefined;
}

function clampFps(fps: number): number {
  return Math.min(MAX_FPS, Math.max(MIN_FPS, Math.round(fps)));
}

/** FPS used for `viewport`: DICOM rate when auto-fps is on and the series has one, else the slider. */
export function effectiveFps(viewport: Viewport | null | undefined): number {
  const dicom = autoFps ? dicomFrameRate(viewport?.currentStack?.series) : undefined;
  return clampFps(dicom ?? manualFps);
}

function startOn(viewport: Viewport): void {
  window.cornerstoneTools.playClip(viewport.element, effectiveFps(viewport));
  playingOn = viewport;
  viewport.container.classList.add('cine-playing');
  setCineIndicator(viewport, true);
}

/** Stops cine wherever it plays. */
export function stopCine(): void {
  if (!playingOn) return;
  try {
    window.cornerstoneTools.stopClip(playingOn.element);
  } catch (error) {
    console.warn('[viewer] stopClip failed', error);
  }
  playingOn.container.classList.remove('cine-playing');
  setCineIndicator(playingOn, false);
  playingOn = null;
  notify();
}

/** F / play button: pause if playing, else play the active viewport's stack (loops). */
export function toggleCine(): void {
  if (playingOn) {
    stopCine();
    return;
  }
  const viewport = shellRef?.activeViewport();
  const stack = viewport?.currentStack;
  if (!viewport || !stack || !viewport.currentImage() || stack.imageIds.length < 2) return;
  startOn(viewport);
  notify();
}

function restartWithNewRate(): void {
  if (!playingOn) return;
  const viewport = playingOn;
  window.cornerstoneTools.stopClip(viewport.element);
  startOn(viewport);
}

function refreshBar(): void {
  if (!bar) return;
  bar.hidden = !barVisible;
  const play = bar.querySelector<HTMLButtonElement>('.cine-play');
  if (play) {
    const playing = isCinePlaying();
    play.classList.toggle('playing', playing);
    play.title = playing ? 'Pausar' : 'Reproducir';
    play.setAttribute('aria-label', play.title);
  }
  const target = playingOn ?? shellRef?.activeViewport();
  const fps = effectiveFps(target);
  const range = bar.querySelector<HTMLInputElement>('.cine-fps');
  const readout = bar.querySelector<HTMLElement>('.cine-fps-value');
  const usingDicom = autoFps && dicomFrameRate(target?.currentStack?.series) !== undefined;
  if (range) {
    range.value = String(fps);
    range.disabled = usingDicom;
  }
  if (readout) readout.textContent = `${fps} FPS`;
}

/** Builds the cine bar (hidden until " Cine" is picked) inside the toolbar at `host`. */
export function mountCineBar(host: HTMLElement): HTMLElement {
  bar = document.createElement('div');
  bar.className = 'cine-bar';
  bar.id = 'tb-cine-bar';
  const play = document.createElement('button');
  play.type = 'button';
  play.className = 'toolbar-button cine-play';
  play.addEventListener('click', () => {
    toggleCine();
  });
  play.addEventListener('touchend', (event) => {
    event.preventDefault();
    toggleCine();
  });
  const range = document.createElement('input');
  range.type = 'range';
  range.className = 'cine-fps';
  range.min = String(MIN_FPS);
  range.max = String(MAX_FPS);
  range.step = String(FPS_STEP);
  range.value = String(DEFAULT_FPS);
  range.title = 'FPS';
  range.setAttribute('aria-label', 'FPS');
  range.addEventListener('input', () => {
    manualFps = clampFps(Number(range.value));
    refreshBar();
    restartWithNewRate();
  });
  const readout = document.createElement('span');
  readout.className = 'cine-fps-value';
  const autoLabel = document.createElement('label');
  autoLabel.className = 'cine-auto';
  const auto = document.createElement('input');
  auto.type = 'checkbox';
  auto.className = 'cine-auto-fps';
  auto.checked = autoFps;
  auto.addEventListener('change', () => {
    autoFps = auto.checked;
    refreshBar();
    restartWithNewRate();
  });
  autoLabel.append(auto, document.createTextNode(' Auto FPS'));
  bar.append(play, range, readout, autoLabel);
  host.appendChild(bar);
  refreshBar();
  return bar;
}

/** Stops cine when its viewport goes away or shows another series. */
export function install(shell: ViewerShell): void {
  shellRef = shell;
  shell.on('layout-changed', () => {
    playingOn = null;
    notify();
  });
  shell.on('series-shown', ({ viewport }) => {
    if (viewport === playingOn) stopCine();
    else refreshBar();
  });
  shell.on('active-changed', () => refreshBar());
}
